import React, { useState, useRef, useEffect, ReactNode } from 'react';

interface TooltipProps {
    children: ReactNode;
    message: string;
    delay?: number;
    position?: 'top' | 'bottom' | 'left' | 'right';
    className?: string;
    arrowColor?: string;
    backgroundColor?: string;
    textColor?: string;
}

const CustomTooltip: React.FC<TooltipProps> = ({
    children,
    message,
    delay = 2000,
    position = 'bottom',
    className = '',
    arrowColor = '#374151',
    backgroundColor = '#374151',
    textColor = '#ffffff',
}) => {
    const [showTooltip, setShowTooltip] = useState(false);
    const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    const handleMouseEnter = () => {
        if (hoverTimeoutRef.current) {
            clearTimeout(hoverTimeoutRef.current);
        }

        hoverTimeoutRef.current = setTimeout(() => {
            setShowTooltip(true);
        }, delay);
    };

    const handleMouseLeave = () => {
        if (hoverTimeoutRef.current) {
            clearTimeout(hoverTimeoutRef.current);
            hoverTimeoutRef.current = null;
        }
        setShowTooltip(false);
    };

    // Cleanup du timeout
    useEffect(() => {
        return () => {
            if (hoverTimeoutRef.current) {
                clearTimeout(hoverTimeoutRef.current);
            }
        };
    }, []);

    // Classes CSS pour les différentes positions
    const getPositionClasses = () => {
        switch (position) {
            case 'top':
                return 'bottom-full left-1/2 transform -translate-x-1/2 mb-2';
            case 'bottom':
                return 'top-full left-1/2 transform -translate-x-1/2 mt-2';
            case 'left':
                return 'right-full top-1/2 transform -translate-y-1/2 mr-2';
            case 'right':
                return 'left-full top-1/2 transform -translate-y-1/2 ml-2';
            default:
                return 'top-full left-1/2 transform -translate-x-1/2 mt-2';
        }
    };

    // Classes CSS pour les flèches selon la position
    const getArrowClasses = () => {
        switch (position) {
            case 'top':
                return `absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-l-transparent border-r-4 border-r-transparent border-t-4 border-t-[${arrowColor}]`;
            case 'bottom':
                return `absolute bottom-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-l-transparent border-r-4 border-r-transparent border-b-4 border-b-[${arrowColor}]`;
            case 'left':
                return `absolute left-full top-1/2 transform -translate-y-1/2 w-0 h-0 border-t-4 border-t-transparent border-b-4 border-b-transparent border-l-4 border-l-[${arrowColor}]`;
            case 'right':
                return `absolute right-full top-1/2 transform -translate-y-1/2 w-0 h-0 border-t-4 border-t-transparent border-b-4 border-b-transparent border-r-4 border-r-[${arrowColor}]`;
            default:
                return `absolute bottom-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-l-transparent border-r-4 border-r-transparent border-b-4 border-b-[${arrowColor}]`;
        }
    };

    return (
        <div className="relative inline-block">
            <div onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
                {children}
            </div>

            {showTooltip && (
                <div className={`absolute z-50 ${getPositionClasses()}`}>
                    <div
                        className={`px-3 py-2 rounded-lg shadow-lg whitespace-nowrap text-sm ${className}`}
                        style={{
                            backgroundColor,
                            color: textColor,
                        }}
                    >
                        {message}
                        <div className={getArrowClasses()}></div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CustomTooltip;
